import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { usePublicTracking } from '@/hooks/usePublicTracking';

const demoFormSchema = z.object({
  nome: z.string().trim()
    .min(2, "Nome muito curto")
    .max(100, "Nome muito longo"),
  telefone: z.string().trim()
    .min(10, "Telefone deve ter pelo menos 10 dígitos")
    .max(20, "Telefone muito longo")
    .regex(/^\d+$/, "Apenas números"),
  empresa: z.string().trim()
    .min(2, "Nome da empresa é obrigatório")
    .max(100),
  quantidadePessoas: z.string()
    .refine(val => val !== "", { message: "Selecione uma opção" }),
  email: z.string().trim()
    .email("E-mail inválido")
    .max(255)
});

type DemoFormData = z.infer<typeof demoFormSchema>;

export default function Demo() {
  const [lastSubmit, setLastSubmit] = useState<number>(0);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { trackPageView, trackClick, trackFormInteraction, trackConversion } = usePublicTracking();

  useEffect(() => {
    trackPageView();
    
    const handleClickEvent = (e: MouseEvent) => trackClick(e);
    document.addEventListener('click', handleClickEvent);
    
    return () => document.removeEventListener('click', handleClickEvent);
  }, []);

  const form = useForm<DemoFormData>({
    resolver: zodResolver(demoFormSchema),
    mode: "onBlur",
    defaultValues: {
      nome: "",
      telefone: "",
      empresa: "",
      quantidadePessoas: "",
      email: ""
    }
  });

  const onSubmit = async (data: DemoFormData) => {
    console.log("✅ Formulário válido, enviando:", data);
    
    const now = Date.now();
    if (now - lastSubmit < 5000) {
      toast({
        title: "Aguarde",
        description: "Aguarde 5 segundos entre envios",
        variant: "destructive"
      });
      return;
    }
    setLastSubmit(now);

    try {
      // Enviar e-mail via Edge Function
      const { error } = await supabase.functions.invoke('enviar-email-demo', {
        body: {
          nome: data.nome,
          telefone: data.telefone,
          empresa: data.empresa,
          quantidadePessoas: data.quantidadePessoas,
          email: data.email
        }
      });
      
      if (error) throw error;
      
      // Toast de sucesso
      toast({
        title: "✅ Enviado com sucesso!",
        description: "Entraremos em contato em breve.",
      });
      
      // Tracking de conversão
      await trackConversion('demo_form_dedicated');
      
      // Redirecionar para página de sucesso
      navigate('/demo/sucesso');
      
    } catch (error) {
      // Erro silencioso - não mostra toast
      console.error("Erro ao enviar:", error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/20 via-background to-secondary/20 p-4">
      {/* Botão de voltar para página inicial */}
      <Button 
        variant="ghost" 
        size="sm"
        onClick={() => navigate('/')}
        className="absolute top-4 left-4 flex items-center gap-2"
      >
        <ArrowLeft className="h-4 w-4" />
        Voltar para início
      </Button>

      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">Agende uma Demo</CardTitle>
          <CardDescription>
            Preencha o formulário e entraremos em contato
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nome">Nome completo</Label>
              <Input 
                id="nome" 
                placeholder="Digite seu nome" 
                {...form.register('nome')}
                onFocus={() => trackFormInteraction('nome')}
              />
              {form.formState.errors.nome && (
                <p className="text-sm text-destructive">{form.formState.errors.nome.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="telefone">Telefone (apenas números)</Label>
              <Input 
                id="telefone" 
                placeholder="11912345678" 
                {...form.register('telefone')}
                onFocus={() => trackFormInteraction('telefone')}
              />
              {form.formState.errors.telefone && (
                <p className="text-sm text-destructive">{form.formState.errors.telefone.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="empresa">Nome da empresa</Label>
              <Input 
                id="empresa" 
                placeholder="Sua empresa" 
                {...form.register('empresa')}
                onFocus={() => trackFormInteraction('empresa')}
              />
              {form.formState.errors.empresa && (
                <p className="text-sm text-destructive">{form.formState.errors.empresa.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="quantidadePessoas">Quantas pessoas na empresa</Label>
              <select 
                id="quantidadePessoas" 
                {...form.register('quantidadePessoas')}
                onFocus={() => trackFormInteraction('quantidadePessoas')}
                className="w-full h-10 px-3 rounded-md border border-input bg-background"
              >
                <option value="">Selecione</option>
                <option value="1-10">1-10 colaboradores</option>
                <option value="11-50">11-50 colaboradores</option>
                <option value="51-200">51-200 colaboradores</option>
                <option value="201+">201+ colaboradores</option>
              </select>
              {form.formState.errors.quantidadePessoas && (
                <p className="text-sm text-destructive">{form.formState.errors.quantidadePessoas.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email corporativo</Label>
              <Input 
                id="email" 
                type="email" 
                placeholder="seu@empresa.com" 
                {...form.register('email')}
                onFocus={() => trackFormInteraction('email')}
              />
              {form.formState.errors.email && (
                <p className="text-sm text-destructive">{form.formState.errors.email.message}</p>
              )}
            </div>

            <Button 
              type="submit" 
              className="w-full" 
              disabled={form.formState.isSubmitting || !form.formState.isValid}
            >
              {form.formState.isSubmitting ? 'Enviando...' : 'Agendar Demo Gratuita'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
